import { useCallback, useMemo, useState } from 'react';
import { getTaskScenarioMeta } from '../../../models/Task/scenarioMapping';
import type { OutgoingCallsRequest } from '../../../store/reducers/OutgoingCallsSlice';
import {
  getSelectedReports,
  type ReportKind,
  type ReportRequest,
  selectReportLoading,
} from '../../../store/reducers/ReportSlice';
import { useAppDispatch, useAppSelector } from '../../../store/store';

export const buildOutgoingReportRequest = (
  request: OutgoingCallsRequest,
): ReportRequest => ({
  From: request['Filter.From'],
  To: request['Filter.To'],
  ClinicIds: request['Filter.ClinicIds'],
  States: request['Filter.States'],
  AttemptStates: request['Filter.AttemptStates'],
  Result: request['Filter.Result'],
  Types: request['Filter.Types'] ?? request['Filter.NotificationTypes'],
  TextSearch: request['Filter.TextSearch'],
  AttemptFrom: request['Filter.AttemptFrom'],
  AttemptTo: request['Filter.AttemptTo'],
  FormTemplateIds: request['Filter.FormTemplateIds'],
  FormBatchIds: request['Filter.FormBatchIds'],
  DispensarizationBatchIds: request['Filter.DispensarizationBatchIds'],
});

const hasSlotOnlySelection = (request: ReportRequest): boolean => {
  const selectedTypes = request.Types ?? [];

  return (
    selectedTypes.length > 0 &&
    selectedTypes.every(
      (taskType) => getTaskScenarioMeta(taskType).group === 'slot',
    )
  );
};

export const useOutgoingReport = (
  request: OutgoingCallsRequest,
  onStandardReport: () => void,
) => {
  const dispatch = useAppDispatch();
  const isReportLoading = useAppSelector(selectReportLoading);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const reportRequest = useMemo(
    () => buildOutgoingReportRequest(request),
    [request],
  );
  const canSelectReportKinds = useMemo(
    () => hasSlotOnlySelection(reportRequest),
    [reportRequest],
  );

  const handleReportClick = useCallback(() => {
    if (canSelectReportKinds) {
      setIsModalOpen(true);
      return;
    }

    onStandardReport();
  }, [canSelectReportKinds, onStandardReport]);

  const handleGenerate = useCallback(
    async (reportKinds: readonly ReportKind[]): Promise<void> => {
      const result = await dispatch(
        getSelectedReports({ request: reportRequest, reportKinds }),
      );

      if (getSelectedReports.fulfilled.match(result)) {
        setIsModalOpen(false);
      }
    },
    [dispatch, reportRequest],
  );

  const handleCancel = useCallback(() => setIsModalOpen(false), []);

  return {
    handleReportClick,
    handleGenerate,
    handleCancel,
    isModalOpen,
    isReportLoading,
  };
};
